/**
 * 사용자 포럼 — 메뉴 커뮤니티 › 사용자 포럼(docs/features/33-user-forum.md, 2026-11-04 사용자 요청).
 *
 *   목록 → 글 보기(답변 · 좋아요 · 신고) → 글쓰기. 한 창(작업 탭) 안에서 오간다.
 *   · 비밀글 — 글쓴이와 관리자만 본다(관리자에게 묻는 글)
 *   · 신고 — 사유를 골라 관리자에게. 서로 다른 사용자 셋이 신고하면 관리자가 보기 전까지 숨는다
 *   · 관리자 — 신고 처리(문제없음 · 숨김 · 삭제) · 논리 삭제 · 「삭제된 글」 에서 복구. 처리 기록이 글마다 남는다
 * 본문은 글 그대로 그린다(HTML 로 해석하지 않는다 — 남이 쓴 글이다). 누가 무엇을 볼 수 있는지는 서버가 정한다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  FiUsers, FiPlus, FiSearch, FiHeart, FiFlag, FiLock, FiEyeOff, FiTrash2, FiRotateCcw, FiArrowLeft, FiEdit3, FiMessageSquare, FiRefreshCw,
} from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { usePrompt } from '../PromptDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/forum';
import './ForumModal.css';

const CATEGORIES = ['question', 'tip', 'request', 'notice'];
const REASONS = ['spam', 'abuse', 'privacy', 'other'];
const PAGE = 30;

/** 신고 — 사유를 고르고(필수) 한마디(선택) */
function ReportBox({ onSubmit, onCancel, t }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  return (
    <div className="frm-report">
      <div className="frm-reportTitle">{t('common:forum.report.title')}</div>
      <div className="frm-reasons">
        {REASONS.map(r => (
          <label key={r} className="frm-reason">
            <input type="radio" name="frm-reason" checked={reason === r} onChange={() => setReason(r)} /> {t(`common:forum.report.reason.${r}`)}
          </label>
        ))}
      </div>
      <input className="frm-input" value={note} maxLength={300} placeholder={t('common:forum.report.notePh')} aria-label={t('common:forum.report.notePh')} onChange={e => setNote(e.target.value)} />
      <div className="frm-row">
        <span className="frm-hint">{t('common:forum.report.hint')}</span>
        <span className="frm-spacer" />
        <button type="button" className="app-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
        <button type="button" className="app-btn app-btn-danger" disabled={!reason} onClick={() => onSubmit({ reason, note })}><FiFlag size={11} /> {t('common:forum.report.send')}</button>
      </div>
    </div>
  );
}
ReportBox.propTypes = { onSubmit: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired, t: PropTypes.func.isRequired };

/** 글·답변의 머리 표시 — 숨김 · 삭제 · 비밀 · 고침 */
function Badges({ item, t }) {
  return (
    <>
      {item.secret ? <span className="frm-badge frm-badge-secret"><FiLock size={10} /> {t('common:forum.secret')}</span> : null}
      {item.hidden ? <span className="frm-badge frm-badge-hidden" title={t('common:forum.hiddenHint')}><FiEyeOff size={10} /> {t('common:forum.hidden')}</span> : null}
      {item.deleted ? <span className="frm-badge frm-badge-deleted"><FiTrash2 size={10} /> {t('common:forum.deleted')}</span> : null}
      {item.openReports ? <span className="frm-badge frm-badge-report"><FiFlag size={10} /> {t('common:forum.openReports', { count: item.openReports })}</span> : null}
    </>
  );
}
Badges.propTypes = { item: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

/** 관리자의 단추 — 신고 처리 · 삭제 · 복구 */
function AdminActions({ item, target, busy, onAct, t }) {
  return (
    <span className="frm-adminActs">
      {item.openReports ? (
        <>
          <button type="button" className="app-btn" disabled={busy} onClick={() => onAct('keep', target, item)}>{t('common:forum.admin.keep')}</button>
          <button type="button" className="app-btn" disabled={busy} onClick={() => onAct('hide', target, item)}><FiEyeOff size={11} /> {t('common:forum.admin.hide')}</button>
        </>
      ) : null}
      {item.deleted
        ? <button type="button" className="app-btn" disabled={busy} onClick={() => onAct('restore', target, item)}><FiRotateCcw size={11} /> {t('common:forum.admin.restore')}</button>
        : <button type="button" className="app-btn app-btn-danger" disabled={busy} onClick={() => onAct('delete', target, item)}><FiTrash2 size={11} /> {t('common:forum.admin.delete')}</button>}
    </span>
  );
}
AdminActions.propTypes = { item: PropTypes.object.isRequired, target: PropTypes.string.isRequired, busy: PropTypes.bool, onAct: PropTypes.func.isRequired, t: PropTypes.func.isRequired };

function Editor({ initial, admin, onSave, onCancel, t }) {
  const [form, setForm] = useState(() => ({ category: 'question', title: '', body: '', secret: false, ...(initial || {}) }));
  const ok = form.title.trim().length >= 2 && form.body.trim().length > 0;
  return (
    <div className="frm-editor">
      <div className="frm-row">
        <select className="frm-select" value={form.category} aria-label={t('common:forum.category')} onChange={e => setForm({ ...form, category: e.target.value })}>
          {CATEGORIES.filter(c => c !== 'notice' || admin).map(c => <option key={c} value={c}>{t(`common:forum.cat.${c}`)}</option>)}
        </select>
        <input className="frm-input frm-titleInput" value={form.title} maxLength={120} placeholder={t('common:forum.titlePh')} aria-label={t('common:forum.titlePh')}
          onChange={e => setForm({ ...form, title: e.target.value })} />
      </div>
      <textarea className="frm-textarea" value={form.body} maxLength={20000} placeholder={t('common:forum.bodyPh')} aria-label={t('common:forum.bodyPh')}
        onChange={e => setForm({ ...form, body: e.target.value })} />
      <div className="frm-warn">{t('common:forum.privacyWarn')}</div>
      <div className="frm-row">
        <label className="frm-check" title={t('common:forum.secretHint')}>
          <input type="checkbox" checked={!!form.secret} onChange={e => setForm({ ...form, secret: e.target.checked })} /> <FiLock size={11} /> {t('common:forum.secretToAdmin')}
        </label>
        <span className="frm-spacer" />
        <button type="button" className="app-btn" onClick={onCancel}>{t('common:ui.cancel')}</button>
        <button type="button" className="app-btn app-btn-primary" disabled={!ok} onClick={() => onSave(form)}>{t('common:forum.post')}</button>
      </div>
    </div>
  );
}
Editor.propTypes = { initial: PropTypes.object, admin: PropTypes.bool, onSave: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired, t: PropTypes.func.isRequired };

export default function ForumModal({ open, onClose, onUnseenChange }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [me, setMe] = useState({ admin: false, trial: false, unseen: {} });
  const [filter, setFilter] = useState({ view: 'all', category: '', sort: 'latest', q: '' });
  const [qInput, setQInput] = useState('');
  const [list, setList] = useState({ total: 0, items: [] });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  // 화면 — list · read(postId) · write(editing 이면 고치기)
  const [screen, setScreen] = useState({ mode: 'list' });
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');
  const [editingReply, setEditingReply] = useState(null);   // { id, body }
  const [reporting, setReporting] = useState(null);          // { target, id }
  const [busy, setBusy] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const r = await api.me();
      setMe({ admin: !!r.admin, trial: !!r.trial, unseen: r.unseen || {} });
      if (onUnseenChange) onUnseenChange(r.unseen || {});
    } catch (_e) { /* 포럼이 없는 판 — 목록이 그 사실을 말한다 */ }
  }, [onUnseenChange]);

  const loadList = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const r = await api.listPosts({ ...filter, category: filter.category || undefined, q: filter.q || undefined, offset: off, limit: PAGE });
      setList({ total: r.total || 0, items: r.items || [] });
      setOffset(off);
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setLoading(false); }
  }, [filter, t]);

  const loadDetail = useCallback(async (id) => {
    try { setDetail(await api.getPost(id)); refreshMe(); } catch (err) { toast.error(formatApiError(err, t)); setScreen({ mode: 'list' }); }
  }, [t, refreshMe]);

  useEffect(() => { if (open) { refreshMe(); } }, [open, refreshMe]);
  useEffect(() => { if (open && screen.mode === 'list') loadList(0); }, [open, screen.mode, loadList]);
  useEffect(() => { if (open && screen.mode === 'read') { setDetail(null); setReply(''); setEditingReply(null); setReporting(null); loadDetail(screen.id); } }, [open, screen, loadDetail]);

  const run = async (fn, after) => {
    setBusy(true);
    try { await fn(); if (after) await after(); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const reloadDetail = () => loadDetail(screen.id);

  const savePost = (form) => run(async () => {
    const r = screen.editing ? await api.editPost(screen.editing.id, form) : await api.createPost(form);
    setScreen({ mode: 'read', id: r.post.id });
  });
  const sendReply = () => run(async () => { await api.addReply(screen.id, reply); setReply(''); }, reloadDetail);
  const saveReply = () => run(async () => { await api.editReply(editingReply.id, editingReply.body); setEditingReply(null); }, reloadDetail);
  const like = (target, id) => run(() => api.toggleLike(target, id), reloadDetail);
  const sendReport = (form) => run(async () => {
    const r = await api.report(reporting.target, reporting.id, form);
    setReporting(null);
    toast.info(t(r.hidden ? 'common:forum.report.sentHidden' : 'common:forum.report.sent'));
    // 글이 숨겨졌으면 신고한 사람에게는 더 보이지 않는다 — 목록으로 돌아간다
    if (r.hidden && reporting.target === 'post' && !me.admin) setScreen({ mode: 'list' }); else await loadDetail(screen.id);
  });

  // 관리자 — 삭제·숨김은 사유를 받는다(기록에 남는다). 되돌릴 수 있지만 남에게 보이는 것이 바뀌니 묻는다
  const adminAct = async (action, target, item) => {
    if (action === 'delete' || action === 'hide') {
      const reason = await prompt({
        title: t(action === 'delete' ? 'common:forum.admin.deleteTitle' : 'common:forum.admin.hideTitle'),
        message: t('common:forum.admin.reasonMsg'), placeholder: t('common:forum.admin.reasonPh'),
        confirmText: t(action === 'delete' ? 'common:forum.admin.delete' : 'common:forum.admin.hide'),
      });
      if (reason == null) return;
      return run(() => (item.openReports ? api.resolveReports(target, item.id, action, reason) : (action === 'delete' ? api.remove(target, item.id, reason) : api.resolveReports(target, item.id, 'hide', reason))),
        async () => { await reloadDetail(); refreshMe(); });
    }
    if (action === 'restore') {
      const ok = await confirm({ title: t('common:forum.admin.restoreTitle'), message: t('common:forum.admin.restoreMsg'), confirmText: t('common:forum.admin.restore') });
      if (!ok) return;
      return run(() => api.restore(target, item.id), reloadDetail);
    }
    return run(() => api.resolveReports(target, item.id, 'keep'), async () => { await reloadDetail(); refreshMe(); });
  };

  const views = ['all', 'mine', ...(me.admin ? ['secret', 'reported', 'deleted'] : [])];
  const p = detail && detail.post;

  return (
    <AppModal windowed open={!!open} onClose={onClose} title={t('menu:community.forum')} icon={<FiUsers size={14} />} className="frm-modal">
      <div className="frm-body">
        {screen.mode === 'list' ? (
          <div className="frm-pane">
            <div className="frm-toolbar">
              <div className="frm-views" role="tablist">
                {views.map(v => (
                  <button key={v} type="button" role="tab" aria-selected={filter.view === v} className={`frm-view${filter.view === v ? ' active' : ''}`} onClick={() => setFilter({ ...filter, view: v })}>
                    {t(`common:forum.view.${v}`)}
                    {v === 'mine' && me.unseen.replies ? <span className="frm-dot">{me.unseen.replies}</span> : null}
                    {v === 'reported' && me.unseen.reports ? <span className="frm-dot">{me.unseen.reports}</span> : null}
                    {v === 'secret' && me.unseen.secretOpen ? <span className="frm-dot">{me.unseen.secretOpen}</span> : null}
                  </button>
                ))}
              </div>
              <span className="frm-spacer" />
              {/* 체험 계정은 읽기만 — 서버도 막는다(FORUM_TRIAL_READONLY) */}
              {me.trial ? null : <button type="button" className="app-btn app-btn-primary" onClick={() => setScreen({ mode: 'write' })}><FiPlus size={12} /> {t('common:forum.write')}</button>}
            </div>
            {me.trial ? <div className="frm-trial">{t('common:forum.trialReadOnly')}</div> : null}
            <div className="frm-filters">
              <select className="frm-select" value={filter.category} aria-label={t('common:forum.category')} onChange={e => setFilter({ ...filter, category: e.target.value })}>
                <option value="">{t('common:forum.cat.all')}</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{t(`common:forum.cat.${c}`)}</option>)}
              </select>
              <select className="frm-select" value={filter.sort} aria-label={t('common:forum.sortLabel')} onChange={e => setFilter({ ...filter, sort: e.target.value })}>
                {['latest', 'likes', 'unanswered'].map(s => <option key={s} value={s}>{t(`common:forum.sort.${s}`)}</option>)}
              </select>
              <label className="frm-search">
                <FiSearch size={12} />
                <input value={qInput} placeholder={t('common:forum.searchPh')} aria-label={t('common:forum.searchPh')} onChange={e => setQInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setFilter({ ...filter, q: qInput.trim() }); }} />
              </label>
              <button type="button" className="app-btn" onClick={() => loadList(offset)} title={t('common:ui.refresh')} aria-label={t('common:ui.refresh')}><FiRefreshCw size={12} /></button>
            </div>
            <div className="frm-listWrap">
              {loading && !list.items.length ? <div className="frm-empty">{t('common:state.loading')}</div> : null}
              {!loading && !list.items.length ? <div className="frm-empty">{t('common:forum.empty')}</div> : null}
              <ul className="frm-list">
                {list.items.map(it => (
                  <li key={it.id}>
                    <button type="button" className={`frm-item frm-cat-${it.category}`} onClick={() => setScreen({ mode: 'read', id: it.id })}>
                      <span className="frm-itemHead">
                        <span className="frm-catChip">{t(`common:forum.cat.${it.category}`)}</span>
                        <span className="frm-itemTitle">{it.title}</span>
                        <Badges item={it} t={t} />
                      </span>
                      <span className="frm-excerpt">{it.excerpt}</span>
                      <span className="frm-meta">
                        <span>{it.authorName || t('common:forum.anon')}</span>
                        <span>{storedTime(it.createdAt, 'short')}</span>
                        <span className="frm-metaNum"><FiMessageSquare size={11} /> {it.replyCount}</span>
                        <span className="frm-metaNum"><FiHeart size={11} /> {it.likeCount}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {list.total > PAGE ? (
              <div className="frm-pager">
                <button type="button" className="app-btn" disabled={offset === 0} onClick={() => loadList(Math.max(0, offset - PAGE))}>{t('common:forum.prev')}</button>
                <span>{t('common:forum.pageOf', { from: offset + 1, to: Math.min(list.total, offset + PAGE), total: list.total })}</span>
                <button type="button" className="app-btn" disabled={offset + PAGE >= list.total} onClick={() => loadList(offset + PAGE)}>{t('common:forum.next')}</button>
              </div>
            ) : null}
          </div>
        ) : null}

        {screen.mode === 'write' ? (
          <div className="frm-pane frm-scroll">
            <button type="button" className="app-btn frm-back" onClick={() => setScreen(screen.editing ? { mode: 'read', id: screen.editing.id } : { mode: 'list' })}><FiArrowLeft size={12} /> {t('common:forum.back')}</button>
            <Editor initial={screen.editing} admin={me.admin} t={t} onSave={savePost}
              onCancel={() => setScreen(screen.editing ? { mode: 'read', id: screen.editing.id } : { mode: 'list' })} />
          </div>
        ) : null}

        {screen.mode === 'read' ? (
          <div className="frm-pane frm-scroll">
            <button type="button" className="app-btn frm-back" onClick={() => setScreen({ mode: 'list' })}><FiArrowLeft size={12} /> {t('common:forum.back')}</button>
            {!p ? <div className="frm-empty">{t('common:state.loading')}</div> : (
              <>
                <article className="frm-post">
                  <div className="frm-itemHead">
                    <span className="frm-catChip">{t(`common:forum.cat.${p.category}`)}</span>
                    <h3 className="frm-postTitle">{p.title}</h3>
                    <Badges item={p} t={t} />
                  </div>
                  <div className="frm-meta">
                    <span>{p.authorName || t('common:forum.anon')}{me.admin && p.authorId ? ` (${p.authorId})` : ''}</span>
                    <span>{storedTime(p.createdAt, 'min')}</span>
                    {p.editedAt ? <span>{t('common:forum.edited')}</span> : null}
                  </div>
                  {p.hidden && p.mine ? <div className="frm-notice">{t('common:forum.hiddenMine')}</div> : null}
                  {p.deleted ? <div className="frm-notice">{t('common:forum.deletedBy', { at: storedTime(p.deletedAt, 'min'), by: p.deletedBy || '', reason: p.deletedReason || '-' })}</div> : null}
                  <div className="frm-text">{p.body}</div>
                  <div className="frm-acts">
                    <button type="button" className={`app-btn${p.liked ? ' frm-liked' : ''}`} disabled={busy || p.mine || p.deleted || me.trial} onClick={() => like('post', p.id)} title={p.mine ? t('common:forum.ownLike') : t('common:forum.like')}>
                      <FiHeart size={11} /> {p.likeCount}
                    </button>
                    {p.mine && !p.deleted ? <button type="button" className="app-btn" onClick={() => setScreen({ mode: 'write', editing: { id: p.id, category: p.category, title: p.title, body: p.body, secret: p.secret } })}><FiEdit3 size={11} /> {t('common:forum.edit')}</button> : null}
                    {!p.mine && !p.deleted && !me.trial ? (
                      <button type="button" className="app-btn" disabled={p.reported} onClick={() => setReporting({ target: 'post', id: p.id })} title={p.reported ? t('common:forum.report.done') : undefined}>
                        <FiFlag size={11} /> {t(p.reported ? 'common:forum.report.done' : 'common:forum.report.btn')}
                      </button>
                    ) : null}
                    <span className="frm-spacer" />
                    {me.admin ? <AdminActions item={p} target="post" busy={busy} onAct={adminAct} t={t} /> : null}
                  </div>
                  {reporting && reporting.target === 'post' ? <ReportBox t={t} onCancel={() => setReporting(null)} onSubmit={sendReport} /> : null}
                </article>

                <h4 className="frm-repliesTitle">{t('common:forum.replies', { count: detail.replies.length })}</h4>
                <ul className="frm-replies">
                  {detail.replies.map(r => (
                    <li key={r.id} className={`frm-reply${r.deleted ? ' frm-isDeleted' : ''}`}>
                      <div className="frm-meta">
                        <span className="frm-replyAuthor">{r.authorName || t('common:forum.anon')}{me.admin && r.authorId ? ` (${r.authorId})` : ''}</span>
                        <span>{storedTime(r.createdAt, 'min')}</span>
                        {r.editedAt ? <span>{t('common:forum.edited')}</span> : null}
                        <Badges item={r} t={t} />
                      </div>
                      {editingReply && editingReply.id === r.id ? (
                        <div className="frm-replyEdit">
                          <textarea className="frm-textarea frm-replyBox" value={editingReply.body} maxLength={10000} aria-label={t('common:forum.edit')} onChange={e => setEditingReply({ ...editingReply, body: e.target.value })} />
                          <div className="frm-row">
                            <span className="frm-spacer" />
                            <button type="button" className="app-btn" onClick={() => setEditingReply(null)}>{t('common:ui.cancel')}</button>
                            <button type="button" className="app-btn app-btn-primary" disabled={busy || !editingReply.body.trim()} onClick={saveReply}>{t('common:action.save')}</button>
                          </div>
                        </div>
                      ) : <div className="frm-text">{r.body}</div>}
                      {r.deleted ? <div className="frm-notice">{t('common:forum.deletedBy', { at: storedTime(r.deletedAt, 'min'), by: r.deletedBy || '', reason: r.deletedReason || '-' })}</div> : null}
                      <div className="frm-acts">
                        <button type="button" className={`app-btn${r.liked ? ' frm-liked' : ''}`} disabled={busy || r.mine || r.deleted || me.trial} onClick={() => like('reply', r.id)} title={r.mine ? t('common:forum.ownLike') : t('common:forum.like')}>
                          <FiHeart size={11} /> {r.likeCount}
                        </button>
                        {r.mine && !r.deleted && !(editingReply && editingReply.id === r.id) ? <button type="button" className="app-btn" onClick={() => setEditingReply({ id: r.id, body: r.body })}><FiEdit3 size={11} /> {t('common:forum.edit')}</button> : null}
                        {!r.mine && !r.deleted && !me.trial ? (
                          <button type="button" className="app-btn" disabled={r.reported} onClick={() => setReporting({ target: 'reply', id: r.id })}>
                            <FiFlag size={11} /> {t(r.reported ? 'common:forum.report.done' : 'common:forum.report.btn')}
                          </button>
                        ) : null}
                        <span className="frm-spacer" />
                        {me.admin ? <AdminActions item={r} target="reply" busy={busy} onAct={adminAct} t={t} /> : null}
                      </div>
                      {reporting && reporting.target === 'reply' && reporting.id === r.id ? <ReportBox t={t} onCancel={() => setReporting(null)} onSubmit={sendReport} /> : null}
                    </li>
                  ))}
                </ul>

                {detail.canReply ? (
                  <div className="frm-replyNew">
                    <textarea className="frm-textarea frm-replyBox" value={reply} maxLength={10000} placeholder={t(p.secret ? 'common:forum.replySecretPh' : 'common:forum.replyPh')} aria-label={t('common:forum.replyPh')}
                      onChange={e => setReply(e.target.value)} />
                    <div className="frm-row">
                      <span className="frm-hint">{t('common:forum.privacyWarn')}</span>
                      <span className="frm-spacer" />
                      <button type="button" className="app-btn app-btn-primary" disabled={busy || !reply.trim()} onClick={sendReply}>{t('common:forum.sendReply')}</button>
                    </div>
                  </div>
                ) : null}

                {me.admin && (detail.reports || []).length ? (
                  <section className="frm-adminLog">
                    <h4>{t('common:forum.admin.reports')}</h4>
                    <ul>
                      {detail.reports.map(r => (
                        <li key={r.id} className={r.resolvedAt ? 'frm-resolved' : ''}>
                          {storedTime(r.createdAt, 'short')} · {t(`common:forum.report.reason.${r.reason}`, { defaultValue: r.reason })} · {r.reporter}
                          {r.note ? ` — ${r.note}` : ''}
                          {r.resolvedAt ? ` → ${t(`common:forum.admin.res.${r.resolution}`, { defaultValue: r.resolution })} (${r.resolvedBy})` : ''}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                {me.admin && (detail.modLog || []).length ? (
                  <section className="frm-adminLog">
                    <h4>{t('common:forum.admin.log')}</h4>
                    <ul>
                      {detail.modLog.map(m => (
                        <li key={m.id}>{storedTime(m.at, 'short')} · {t(`common:forum.admin.act.${m.action}`, { defaultValue: m.action })} · {t(`common:forum.target.${m.target}`)} · {m.admin}{m.reason ? ` — ${m.reason}` : ''}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </div>
    </AppModal>
  );
}

ForumModal.propTypes = { open: PropTypes.bool, onClose: PropTypes.func.isRequired, onUnseenChange: PropTypes.func };
ForumModal.defaultProps = { open: false, onUnseenChange: null };
